// Hard-delete button (admin only). Opens a modal that asks the admin to
// pick a delete mode:
//   - 'preserve_history' (default) — cascade live state but keep frozen
//     monthly_snapshots and audit_log entries so historical reports remain
//     reproducible.
//   - 'full' — wipe everything including snapshots and audit. For test
//     accounts that should leave no trace.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { HardDeleteMode } from '@frt/shared-types';

export function HardDeleteButton({
  entityLabel,
  onConfirm,
}: {
  entityLabel: string;
  onConfirm: (body: { mode: HardDeleteMode; reason: string | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<HardDeleteMode>('preserve_history');
  const [reason, setReason] = useState('');

  return (
    <>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        Delete
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
            <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Delete {entityLabel}</h2>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-700"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <p className="text-slate-700">This is permanent. Pick the mode:</p>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  className="mt-1"
                  checked={mode === 'preserve_history'}
                  onChange={() => setMode('preserve_history')}
                />
                <span>
                  <strong>Preserve history</strong> — drop live records but keep frozen
                  monthly snapshots and audit log entries. Historical reports keep working.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  className="mt-1"
                  checked={mode === 'full'}
                  onChange={() => setMode('full')}
                />
                <span>
                  <strong>Full</strong> — wipe everything including frozen snapshots and
                  audit. Use only for test data.
                </span>
              </label>
              <label className="block">
                <span className="block text-slate-600">Reason (optional)</span>
                <input
                  className="block w-full rounded border border-slate-300 bg-white px-2 py-2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. duplicate test account"
                />
              </label>
            </div>
            <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onConfirm({ mode, reason: reason || null });
                  setOpen(false);
                }}
              >
                Confirm Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
